// utils/logger.ts
/**
 * Logger utility for the application
 *
 * In production, logs are suppressed unless explicitly enabled.
 * In development, all logs are shown.
 *
 * Usage:
 *   import { logger } from '../utils/logger';
 *   logger.info('Something happened');
 *   logger.warn('Warning message');
 *   logger.error('Error occurred', error);
 *   logger.debug('Debug info', data);
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'none';

interface LoggerConfig {
  level: LogLevel;
  enabled: boolean;
}

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
  none: 4,
};

// Determine if we're in production
const isProduction = process.env.NODE_ENV === 'production';

// Default configuration
const config: LoggerConfig = {
  level: isProduction ? 'error' : 'debug',
  enabled: !isProduction,
};

/**
 * Check if a log level should be output
 */
function shouldLog(level: LogLevel): boolean {
  if (!config.enabled) return level === 'error'; // Always log errors
  return LOG_LEVELS[level] >= LOG_LEVELS[config.level];
}

/**
 * Format the log prefix
 */
function formatPrefix(level: LogLevel): string {
  const timestamp = new Date().toISOString();
  return `[${timestamp}] [${level.toUpperCase()}]`;
}

/**
 * Logger object with methods for each log level
 */
export const logger = {
  /**
   * Debug level - detailed information for debugging
   */
  debug(message: string, ...args: unknown[]): void {
    if (shouldLog('debug')) {
      // eslint-disable-next-line no-console
      console.log(formatPrefix('debug'), message, ...args);
    }
  },

  /**
   * Info level - general information
   */
  info(message: string, ...args: unknown[]): void {
    if (shouldLog('info')) {
      // eslint-disable-next-line no-console
      console.info(formatPrefix('info'), message, ...args);
    }
  },

  /**
   * Warn level - warnings that don't prevent operation
   */
  warn(message: string, ...args: unknown[]): void {
    if (shouldLog('warn')) {
      // eslint-disable-next-line no-console
      console.warn(formatPrefix('warn'), message, ...args);
    }
  },

  /**
   * Error level - errors that need attention
   */
  error(message: string, ...args: unknown[]): void {
    if (shouldLog('error')) {
      // eslint-disable-next-line no-console
      console.error(formatPrefix('error'), message, ...args);
    }
  },

  /**
   * Configure the logger
   */
  configure(options: Partial<LoggerConfig>): void {
    if (options.level !== undefined) config.level = options.level;
    if (options.enabled !== undefined) config.enabled = options.enabled;
  },

  /**
   * Enable all logging
   */
  enable(): void {
    config.enabled = true;
  },

  /**
   * Disable all logging (except errors)
   */
  disable(): void {
    config.enabled = false;
  },

  /**
   * Set log level
   */
  setLevel(level: LogLevel): void {
    config.level = level;
  },
};

export default logger;
